import { useEffect, useMemo, useState } from "react";
import { normalizeTrackNameForLookup } from "@/utils/future-tips-utils";
import { logger } from "@/utils/logger";
import { formatTimeOfDay } from "@/utils/time-format";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  MedalIcon,
  TrophyIcon,
} from "lucide-react";
import TableSkeleton from "@/components/ui/TableSkeleton";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const ResultsArchiveTable = ({
  archiveData,
  isLoading,
  error,
  trackName,
  showMaxHedge,
  setShowMaxHedge,
  maxHedgeWinRate,
  setMaxHedgeWinRate,
  onFilteredRacesChange,
  showEarlyMailTips = false,
  earlyMailTipsLookup = null,
  hideDateColumn = false,
}) => {
  // Debug logging to understand data structure
  logger.log("ResultsArchiveTable - archiveData:", archiveData);
  logger.log(
    "ResultsArchiveTable - archiveData.data type:",
    typeof archiveData?.data,
  );
  logger.log(
    "ResultsArchiveTable - archiveData.data sample:",
    archiveData?.data?.[0],
  );

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <Card className="p-4 text-center text-red-500">
        Error loading archive data: {error.message}
      </Card>
    );
  }

  const [selectedDate, setSelectedDate] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "time",
    direction: "asc",
  });

  const getRaceDate = (race) => {
    if (!race || typeof race !== "object") return "";

    // First try to get date directly
    if (race.date && typeof race.date === "string") return race.date;

    // Try to extract from race ID
    const raceId = race.id || "";
    if (typeof raceId !== "string") return "";

    const parts = raceId.split("-");
    if (parts.length < 3) return "";

    const dd = parts[parts.length - 3];
    const mm = parts[parts.length - 2];
    const yyyy = parts[parts.length - 1];

    // Validate the extracted parts
    if (
      dd &&
      mm &&
      yyyy &&
      /^\d{1,2}$/.test(dd) &&
      /^\d{1,2}$/.test(mm) &&
      /^\d{4}$/.test(yyyy)
    ) {
      return `${dd}-${mm}-${yyyy}`;
    }

    return "";
  };

  const buildDateFromParts = (dateStr) => {
    if (!dateStr) return null;
    const [dd, mm, yyyy] = dateStr.split("-");
    if (!dd || !mm || !yyyy) return null;
    const iso = `${yyyy}-${mm}-${dd}T00:00:00`;
    const parsed = new Date(iso);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const parseDateTimeValue = (value) => {
    if (!value) return null;
    if (typeof value === "number") {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }
    if (typeof value === "object") {
      if (typeof value.seconds === "number") {
        const date = new Date(
          value.seconds * 1000 +
            (value.nanoseconds ? value.nanoseconds / 1e6 : 0),
        );
        return isNaN(date.getTime()) ? null : date;
      }
      if (typeof value._seconds === "number") {
        const date = new Date(
          value._seconds * 1000 +
            (value._nanoseconds ? value._nanoseconds / 1e6 : 0),
        );
        return isNaN(date.getTime()) ? null : date;
      }
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed.length) return null;
      if (/^\d{1,2}:\d{2}/.test(trimmed)) {
        return trimmed;
      }
      const normalized = trimmed.includes("T")
        ? trimmed
        : trimmed.replace(" ", "T");
      const parsed = new Date(normalized);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const mergeDateAndTime = (baseDate, timeStr) => {
    if (!baseDate || !timeStr) return null;
    const [hh, mm] = timeStr.split(":");
    if (hh === undefined || mm === undefined) return null;
    const merged = new Date(baseDate);
    const hours = Number(hh);
    const minutes = Number(mm);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    merged.setHours(hours, minutes, 0, 0);
    return merged;
  };

  const getRaceDateTime = (race) => {
    const dateStr = getRaceDate(race);
    const baseDate = buildDateFromParts(dateStr);
    const candidates = [
      race.predictionData?.marketStartTime,
      race.earlyPredictionData?.marketStartTime,
      race.betfairResults?.raceStartTime,
      race.betfairResults?.raceStart,
      race.betfairResults?.startTime,
      race.betfairResults?.marketStartTime,
      race.marketStartTime,
      race.startTime,
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;
      if (
        typeof candidate === "string" &&
        /^\d{1,2}:\d{2}/.test(candidate.trim()) &&
        baseDate
      ) {
        const merged = mergeDateAndTime(baseDate, candidate.trim());
        if (merged) return merged;
      }
      const parsed = parseDateTimeValue(candidate);
      if (parsed instanceof Date && !isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    return baseDate;
  };

  // Site-wide time format: "1:10 pm" in Sydney time
  const getRaceTimeDisplay = (race) => {
    const dateTime = getRaceDateTime(race);
    if (!dateTime || isNaN(dateTime.getTime())) return "—";
    return formatTimeOfDay(dateTime) || "—";
  };

  const availableDates = useMemo(() => {
    const list = (archiveData?.data || [])
      .map(getRaceDate)
      .filter((date) => date && typeof date === "string" && date.includes("-"));
    const unique = Array.from(new Set(list));
    const toDate = (str) => {
      if (!str || typeof str !== "string") {
        logger.warn("Invalid date string passed to toDate:", str);
        return new Date(0); // Return epoch date for invalid inputs
      }
      const parts = str.split("-");
      if (parts.length !== 3) {
        logger.warn("Invalid date format passed to toDate:", str);
        return new Date(0);
      }
      const [dd, mm, yyyy] = parts;
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    };
    return unique.sort((a, b) => toDate(b) - toDate(a));
  }, [archiveData]);

  const filteredRaces = useMemo(() => {
    const races = archiveData?.data || [];

    // Filter out races that lack BOTH earlyPredictionData.firstFourData AND predictionData.firstFourData
    const racesWithValidData = races.filter((race) => {
      const hasEarlyData =
        race.earlyPredictionData?.firstFourData &&
        Array.isArray(race.earlyPredictionData.firstFourData) &&
        race.earlyPredictionData.firstFourData.length > 0;

      const hasPredictionData =
        race.predictionData?.firstFourData &&
        Array.isArray(race.predictionData.firstFourData) &&
        race.predictionData.firstFourData.length > 0;

      // Include race if it has either early data OR prediction data
      return hasEarlyData || hasPredictionData;
    });

    const filtered = !selectedDate
      ? racesWithValidData
      : racesWithValidData.filter((race) => getRaceDate(race) === selectedDate);

    const toDate = (str) => {
      if (!str) return new Date(NaN);
      const [dd, mm, yyyy] = str.split("-");
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    };

    // Apply sorting if configured
    let sorted = filtered.slice();

    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case "date":
            aValue = toDate(getRaceDate(a)).getTime() || 0;
            bValue = toDate(getRaceDate(b)).getTime() || 0;
            break;
          case "time":
            aValue = getRaceDateTime(a)?.getTime() || 0;
            bValue = getRaceDateTime(b)?.getTime() || 0;
            break;
          case "uniqueTips": {
            const { tipNumbers: aTipNumbers } = getTipsData(a);
            const { tipNumbers: bTipNumbers } = getTipsData(b);
            aValue = calculateUniqueTips(aTipNumbers);
            bValue = calculateUniqueTips(bTipNumbers);
            break;
          }
          case "track":
            aValue = a.meetingName || "";
            bValue = b.meetingName || "";
            break;
          case "race":
            const aParts = (a.id || "").split("-");
            const bParts = (b.id || "").split("-");
            aValue = parseInt(aParts[aParts.length - 4], 10) || 0;
            bValue = parseInt(bParts[bParts.length - 4], 10) || 0;
            break;
          case "horses":
            aValue = a.betfairResults?.numberOfRunners || 0;
            bValue = b.betfairResults?.numberOfRunners || 0;
            break;
          case "morningMaxHedge":
            const aEarlyMorning = Array.isArray(
              a.earlyPredictionData?.firstFourData,
            )
              ? a.earlyPredictionData.firstFourData
              : [];
            const bEarlyMorning = Array.isArray(
              b.earlyPredictionData?.firstFourData,
            )
              ? b.earlyPredictionData.firstFourData
              : [];
            aValue = parseFloat(calculateMorningMaxHedge(aEarlyMorning)) || 0;
            bValue = parseFloat(calculateMorningMaxHedge(bEarlyMorning)) || 0;
            break;
          case "raceTimeMaxHedge":
            const aRaceTime = Array.isArray(a.predictionData?.firstFourData)
              ? a.predictionData.firstFourData
              : [];
            const bRaceTime = Array.isArray(b.predictionData?.firstFourData)
              ? b.predictionData.firstFourData
              : [];
            aValue = parseFloat(calculateRaceTimeMaxHedge(aRaceTime)) || 0;
            bValue = parseFloat(calculateRaceTimeMaxHedge(bRaceTime)) || 0;
            break;
          case "earlyMailMaxHedge": {
            const extractRaceNumber = (race) => {
              const id = race?.id || "";
              if (typeof id !== "string") return "";
              const parts = id.split("-");
              return parts.length >= 4 ? parts[parts.length - 4] || "" : "";
            };

            const getEarlyMailTips = (race) => {
              if (!showEarlyMailColumns) return [];
              const raceNumber = extractRaceNumber(race);
              const trackName =
                race?.meetingName ||
                (typeof race?.id === "string"
                  ? race.id
                      .split("-")
                      .slice(0, race.id.split("-").length - 4)
                      .join(" ")
                  : "");
              if (!raceNumber || !trackName) return [];
              const key = `${normalizeTrackNameForLookup(trackName)}|${raceNumber}`;
              const found = earlyMailTipsLookup?.get?.(key);
              return Array.isArray(found) ? found : [];
            };

            aValue =
              parseFloat(calculateEarlyMailMaxHedge(getEarlyMailTips(a))) || 0;
            bValue =
              parseFloat(calculateEarlyMailMaxHedge(getEarlyMailTips(b))) || 0;
            break;
          }
          case "combinedMaxHedge":
            const aEarly = Array.isArray(a.earlyPredictionData?.firstFourData)
              ? a.earlyPredictionData.firstFourData
              : [];
            const aRace = Array.isArray(a.predictionData?.firstFourData)
              ? a.predictionData.firstFourData
              : [];
            const bEarly = Array.isArray(b.earlyPredictionData?.firstFourData)
              ? b.earlyPredictionData.firstFourData
              : [];
            const bRace = Array.isArray(b.predictionData?.firstFourData)
              ? b.predictionData.firstFourData
              : [];
            aValue = parseFloat(calculateCombinedMaxHedge(aEarly, aRace)) || 0;
            bValue = parseFloat(calculateCombinedMaxHedge(bEarly, bRace)) || 0;
            break;
          case "maxHedge":
            // Legacy support - use combined max hedge
            const aEarlyLegacy = Array.isArray(
              a.earlyPredictionData?.firstFourData,
            )
              ? a.earlyPredictionData.firstFourData
              : [];
            const aRaceLegacy = Array.isArray(a.predictionData?.firstFourData)
              ? a.predictionData.firstFourData
              : [];
            const bEarlyLegacy = Array.isArray(
              b.earlyPredictionData?.firstFourData,
            )
              ? b.earlyPredictionData.firstFourData
              : [];
            const bRaceLegacy = Array.isArray(b.predictionData?.firstFourData)
              ? b.predictionData.firstFourData
              : [];
            aValue =
              parseFloat(
                calculateCombinedMaxHedge(aEarlyLegacy, aRaceLegacy),
              ) || 0;
            bValue =
              parseFloat(
                calculateCombinedMaxHedge(bEarlyLegacy, bRaceLegacy),
              ) || 0;
            break;
          case "first":
            const aWinner = a.betfairResults?.winner;
            const bWinner = b.betfairResults?.winner;
            const aEarlyData = Array.isArray(
              a.earlyPredictionData?.firstFourData,
            )
              ? a.earlyPredictionData.firstFourData
              : [];
            const aRaceData = Array.isArray(a.predictionData?.firstFourData)
              ? a.predictionData.firstFourData
              : [];
            const bEarlyData = Array.isArray(
              b.earlyPredictionData?.firstFourData,
            )
              ? b.earlyPredictionData.firstFourData
              : [];
            const bRaceData = Array.isArray(b.predictionData?.firstFourData)
              ? b.predictionData.firstFourData
              : [];
            const aAllTips = [...aEarlyData, ...aRaceData];
            const bAllTips = [...bEarlyData, ...bRaceData];
            aValue = aWinner
              ? getHorseNumber(aWinner.selectionId, aAllTips)
              : "-";
            bValue = bWinner
              ? getHorseNumber(bWinner.selectionId, bAllTips)
              : "-";
            break;
          case "winPayout": {
            const parseWin = (race) => {
              const price = parseFloat(
                race.betfairResults?.winner?.startingPrice,
              );
              return Number.isFinite(price) ? price : 0;
            };
            aValue = parseWin(a);
            bValue = parseWin(b);
            break;
          }
          default:
            return 0;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          const comparison = aValue.localeCompare(bValue);
          return sortConfig.direction === "asc" ? comparison : -comparison;
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    } else {
      // Default sorting: date desc, then race number asc, then track name
      sorted.sort((a, b) => {
        const aDateStr = getRaceDate(a);
        const bDateStr = getRaceDate(b);
        const aTime = toDate(aDateStr).getTime();
        const bTime = toDate(bDateStr).getTime();
        const safeATime = isNaN(aTime) ? 0 : aTime;
        const safeBTime = isNaN(bTime) ? 0 : bTime;

        if (safeBTime !== safeATime) return safeBTime - safeATime; // date desc

        const aParts = (a.id || "").split("-");
        const bParts = (b.id || "").split("-");
        const aRaceNum = parseInt(aParts[aParts.length - 4], 10) || 0;
        const bRaceNum = parseInt(bParts[bParts.length - 4], 10) || 0;
        if (aRaceNum !== bRaceNum) return aRaceNum - bRaceNum; // race # asc

        const aTrack =
          a.meetingName || aParts.slice(0, aParts.length - 4).join(" ");
        const bTrack =
          b.meetingName || bParts.slice(0, bParts.length - 4).join(" ");
        return (aTrack || "").localeCompare(bTrack || "");
      });
    }

    return sorted;
  }, [archiveData, selectedDate, sortConfig]);

  useEffect(() => {
    if (typeof onFilteredRacesChange !== "function") return;
    onFilteredRacesChange(filteredRaces);
  }, [filteredRaces, onFilteredRacesChange]);

  // Calculate morning max hedge (T1-T4)
  function calculateMorningMaxHedge(earlyTips) {
    if (!Array.isArray(earlyTips)) return "-";

    let reciprocalSum = 0;
    earlyTips.forEach((tip) => {
      if (tip?.formattedName) {
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (odds && !isNaN(odds) && odds > 0) {
          reciprocalSum += 1 / parseFloat(odds);
        }
      }
    });

    return reciprocalSum > 0 ? reciprocalSum.toFixed(3) : "-";
  }

  // Calculate early mail max hedge (E1-E4)
  function calculateEarlyMailMaxHedge(earlyMailTips) {
    if (!Array.isArray(earlyMailTips)) return "-";

    let reciprocalSum = 0;
    earlyMailTips.forEach((tip) => {
      if (tip?.formattedName) {
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (odds && !isNaN(odds) && odds > 0) {
          reciprocalSum += 1 / parseFloat(odds);
        }
      }
    });

    return reciprocalSum > 0 ? reciprocalSum.toFixed(3) : "-";
  }

  // Calculate race-time max hedge (T5-T8)
  function calculateRaceTimeMaxHedge(raceTips) {
    if (!Array.isArray(raceTips)) return "-";

    let reciprocalSum = 0;
    raceTips.forEach((tip) => {
      if (tip?.formattedName) {
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (odds && !isNaN(odds) && odds > 0) {
          reciprocalSum += 1 / parseFloat(odds);
        }
      }
    });

    return reciprocalSum > 0 ? reciprocalSum.toFixed(3) : "-";
  }

  // Calculate combined max hedge (unique tips with bias toward race-time odds)
  function calculateCombinedMaxHedge(earlyTips, raceTips) {
    if (!Array.isArray(earlyTips) || !Array.isArray(raceTips)) return "-";

    const uniqueTips = new Map();

    // First add morning tips
    earlyTips.forEach((tip) => {
      if (tip?.formattedName) {
        const horseNumber = tip.formattedName.split(".")[0];
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (horseNumber !== "-" && odds && !isNaN(odds) && odds > 0) {
          uniqueTips.set(horseNumber, {
            odds: parseFloat(odds),
            source: "morning",
          });
        }
      }
    });

    // Then add race-time tips (will override morning tips if same horse)
    raceTips.forEach((tip) => {
      if (tip?.formattedName) {
        const horseNumber = tip.formattedName.split(".")[0];
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (horseNumber !== "-" && odds && !isNaN(odds) && odds > 0) {
          uniqueTips.set(horseNumber, {
            odds: parseFloat(odds),
            source: "raceTime",
          });
        }
      }
    });

    if (uniqueTips.size === 0) return "-";

    // Calculate reciprocal sum
    let reciprocalSum = 0;
    uniqueTips.forEach((tipData) => {
      reciprocalSum += 1 / tipData.odds;
    });

    return reciprocalSum.toFixed(3);
  }

  // Legacy function for backward compatibility
  function calculateMaxHedge(earlyTips, raceTips) {
    return calculateCombinedMaxHedge(earlyTips, raceTips);
  }

  // Helper function to check if any tips in a set won the race
  const checkTipsWon = (tips, winnerSelectionId) => {
    if (!Array.isArray(tips) || !winnerSelectionId)
      return { won: false, winningTip: null };

    const winningTip = tips.find(
      (tip) => tip?.selectionId === winnerSelectionId,
    );
    return { won: !!winningTip, winningTip };
  };

  const normalizeSelectionId = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    return str.length ? str : null;
  };

  const normalizeHorseNumber = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str.length) return null;
    const withoutLeadingZeros = str.replace(/^0+/, "");
    return withoutLeadingZeros.length ? withoutLeadingZeros : "0";
  };

  const extractHorseNumber = (entry) => {
    if (!entry) return null;

    const formattedName =
      entry.formattedName ||
      entry.selectionName ||
      entry.runnerName ||
      entry.name;
    if (typeof formattedName === "string") {
      const leadingNumber = formattedName.match(/^(\d+)[\s.\-_:]/);
      const candidate = leadingNumber
        ? leadingNumber[1]
        : formattedName.split(".")[0];
      const normalized = normalizeHorseNumber(candidate);
      if (normalized) return normalized;
    }

    const fromFields =
      entry.horseNumber ??
      entry.runnerNumber ??
      entry.raceNumber ??
      entry.number;
    const normalizedField = normalizeHorseNumber(fromFields);
    if (normalizedField) return normalizedField;

    return null;
  };

  const parseOddsNumber = (value) => {
    if (value === null || value === undefined) return NaN;
    if (typeof value === "number") {
      if (!Number.isFinite(value) || value <= 0) return NaN;
      return value;
    }
    const cleaned = String(value).replace(/[^0-9.\-]/g, "");
    if (!cleaned) return NaN;
    const parsed = parseFloat(cleaned);
    if (!Number.isFinite(parsed) || parsed <= 0) return NaN;
    return parsed;
  };

  const extractTipOdds = (tip) => {
    if (!tip || typeof tip !== "object") {
      return { value: null, source: null, raw: null };
    }

    const candidateList = [
      { value: tip.lastPriceTraded, source: "lastPriceTraded" },
      { value: tip.currentPrice, source: "currentPrice" },
      { value: tip.price?.win, source: "price.win" },
      { value: tip.price?.winPrice, source: "price.winPrice" },
      { value: tip.price, source: "price" },
      { value: tip.winReturn, source: "winReturn" },
      { value: tip.winReturns, source: "winReturns" },
      { value: tip.returns, source: "returns" },
      { value: tip.totalReturns, source: "totalReturns" },
      { value: tip.startingPrice, source: "startingPrice" },
      { value: tip.sp, source: "sp" },
      { value: tip.odds, source: "odds" },
    ];

    for (const candidate of candidateList) {
      const parsed = parseOddsNumber(candidate.value);
      if (Number.isFinite(parsed)) {
        return {
          value: parsed,
          source: candidate.source,
          raw: candidate.value,
        };
      }
    }

    return { value: null, source: null, raw: null };
  };

  const calculateUniqueTips = (tips) => {
    // Filter out empty tips and get unique horse numbers
    const validTips = tips.filter((tip) => tip !== "-" && tip !== "");
    const uniqueTips = [...new Set(validTips)];
    return uniqueTips.length;
  };

  function getTipsData(race) {
    const earlyTips = Array.isArray(race.earlyPredictionData?.firstFourData)
      ? race.earlyPredictionData.firstFourData
      : [];
    const raceTips = Array.isArray(race.predictionData?.firstFourData)
      ? race.predictionData.firstFourData
      : [];

    const tipNumbers = [
      earlyTips[0]?.formattedName?.split(".")[0] || "-",
      earlyTips[1]?.formattedName?.split(".")[0] || "-",
      earlyTips[2]?.formattedName?.split(".")[0] || "-",
      earlyTips[3]?.formattedName?.split(".")[0] || "-",
      raceTips[0]?.formattedName?.split(".")[0] || "-",
      raceTips[1]?.formattedName?.split(".")[0] || "-",
      raceTips[2]?.formattedName?.split(".")[0] || "-",
      raceTips[3]?.formattedName?.split(".")[0] || "-",
    ];

    return { earlyTips, raceTips, tipNumbers };
  }

  function getHorseNumber(selectionId, allTips) {
    if (!selectionId || !allTips?.length) return "-";
    const horse = allTips.find((tip) => tip.selectionId === selectionId);
    return horse?.formattedName?.split(".")[0] || "-";
  }

  // Check if tip has fluctuated
  const getTipFluctuation = (earlyTips, raceTips, horseNumber) => {
    if (!Array.isArray(earlyTips) || !Array.isArray(raceTips)) return null;

    const earlyPosition = earlyTips.findIndex(
      (tip) => tip?.formattedName?.split(".")[0] === horseNumber,
    );
    const racePosition = raceTips.findIndex(
      (tip) => tip?.formattedName?.split(".")[0] === horseNumber,
    );

    if (earlyPosition === -1 || racePosition === -1) return null;

    const earlyRank = earlyPosition + 1;
    const raceRank = racePosition + 1;

    if (earlyRank < raceRank) return "down";
    if (earlyRank > raceRank) return "up";
    return "same";
  };

  // Calculate chance of winning based on number of horses
  const calculateWinChance = (numberOfRunners) => {
    if (!numberOfRunners || numberOfRunners === "-") return null;
    return ((1 / numberOfRunners) * 100).toFixed(1);
  };

  // Calculate implied probability from odds
  const calculateImpliedProbability = (odds) => {
    if (!odds || isNaN(odds)) return null;
    return ((1 / odds) * 100).toFixed(1);
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="w-3 h-3 text-gray-600" />
    ) : (
      <ChevronDown className="w-3 h-3 text-gray-600" />
    );
  };

  const tipKeys = ["T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8"];
  const earlyTipKeys = tipKeys.slice(0, 4);
  const raceTipKeys = tipKeys.slice(4);
  const stickyCellClass =
    "sticky bottom-0 z-10 bg-gray-50 border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]";
  const tipTotalsCellClassEarly = `${stickyCellClass} px-2 py-3 text-center bg-blue-50 border-x border-gray-200`;
  const tipTotalsCellClassRace = `${stickyCellClass} px-2 py-3 text-center bg-purple-50 border-x border-gray-200`;
  const tipTotalsCellClassEarlyMail = `${stickyCellClass} px-2 py-3 text-center bg-emerald-50 border-x border-gray-200`;

  const showEarlyMailColumns = Boolean(
    showEarlyMailTips &&
      earlyMailTipsLookup &&
      earlyMailTipsLookup instanceof Map &&
      earlyMailTipsLookup.size > 0,
  );

  const formatInteger = (value) => {
    const numeric = Number(value || 0);
    return Number.isFinite(numeric) ? numeric.toLocaleString() : "0";
  };

  const formatCurrency = (value) => {
    const numeric = Number(value || 0);
    return numeric > 0 ? `$${numeric.toFixed(2)}` : "-";
  };

  const formatRoiPercentage = (returns, bets) => {
    if (!bets) return "ROI —";
    const roi = ((returns - bets) / bets) * 100;
    const sign = roi >= 0 ? "+" : "-";
    return `ROI ${sign}${Math.abs(roi).toFixed(1)}%`;
  };

  const columnTotalsResult = useMemo(() => {
    const safeRaces = Array.isArray(filteredRaces) ? filteredRaces : [];
    const earlyMailKeys = ["E1", "E2", "E3", "E4"];
    const totalsTipKeys = showEarlyMailColumns
      ? [...earlyMailKeys, ...tipKeys]
      : tipKeys;

    const initialTipWins = totalsTipKeys.reduce((acc, key) => {
      acc[key] = { count: 0, value: 0, bets: 0 };
      return acc;
    }, {});

    const tipTotalsDebug = totalsTipKeys.reduce((acc, key) => {
      acc[key] = {
        count: 0,
        totalValue: 0,
        entries: [],
      };
      return acc;
    }, {});

    const raceDebugSummaries = [];
    const winDebugEntries = [];

    const totals = {
      races: safeRaces.length,
      horses: 0,
      tipWins: initialTipWins,
      tipsTotal: 0,
      winTotal: 0,
      place1Total: 0,
      place2Total: 0,
      place3Total: 0,
    };

    safeRaces.forEach((race) => {
      const betfairResults = race?.betfairResults || {};
      const winner = betfairResults.winner;
      const winnerSelectionIdRaw = winner?.selectionId;
      const winnerSelectionId = normalizeSelectionId(winnerSelectionIdRaw);
      const winPrice = parseOddsNumber(winner?.startingPrice);

      const runners = Number.parseInt(betfairResults?.numberOfRunners, 10);
      if (Number.isFinite(runners)) {
        totals.horses += runners;
      }

      const placeGettersAll = Array.isArray(betfairResults?.placeGetters)
        ? betfairResults.placeGetters
        : [];
      const winnerSelectionIdNorm = normalizeSelectionId(winnerSelectionId);
      const orderedPlaceSlots = (() => {
        if (!placeGettersAll.length) return [];
        const winnerEntry = winnerSelectionIdNorm
          ? placeGettersAll.find(
              (p) =>
                normalizeSelectionId(p?.selectionId) === winnerSelectionIdNorm,
            )
          : null;
        const others = winnerSelectionIdNorm
          ? placeGettersAll.filter(
              (p) =>
                normalizeSelectionId(p?.selectionId) !== winnerSelectionIdNorm,
            )
          : placeGettersAll.slice(1);
        return winnerEntry ? [winnerEntry, ...others] : placeGettersAll;
      })();

      const p1Price = parseOddsNumber(orderedPlaceSlots[0]?.startingPrice);
      if (Number.isFinite(p1Price)) {
        totals.place1Total += p1Price;
      }

      const p2Price = parseOddsNumber(orderedPlaceSlots[1]?.startingPrice);
      if (Number.isFinite(p2Price)) {
        totals.place2Total += p2Price;
      }

      const p3Price = parseOddsNumber(orderedPlaceSlots[2]?.startingPrice);
      if (Number.isFinite(p3Price)) {
        totals.place3Total += p3Price;
      }

      const earlyTips = Array.isArray(race?.earlyPredictionData?.firstFourData)
        ? race.earlyPredictionData.firstFourData
        : [];
      const raceTips = Array.isArray(race?.predictionData?.firstFourData)
        ? race.predictionData.firstFourData
        : [];

      const debugRaceId = race?.id || "(unknown race id)";
      const raceIdParts =
        typeof debugRaceId === "string" ? debugRaceId.split("-") : [];
      const raceNumber =
        raceIdParts.length >= 4 ? raceIdParts[raceIdParts.length - 4] : "";
      const debugTrack =
        race?.meetingName ||
        raceIdParts.slice(0, raceIdParts.length - 4).join(" ");
      const winnerDebugLines = [];
      const tipHitsForRace = [];

      const earlyMailTips = (() => {
        if (!showEarlyMailColumns) return [];
        if (!raceNumber || !debugTrack) return [];
        const lookupKey = `${normalizeTrackNameForLookup(debugTrack)}|${raceNumber}`;
        const found = earlyMailTipsLookup?.get?.(lookupKey);
        return Array.isArray(found) ? found : [];
      })();

      const tipData = [
        ...(showEarlyMailColumns
          ? [
              { key: "E1", tip: earlyMailTips[0] },
              { key: "E2", tip: earlyMailTips[1] },
              { key: "E3", tip: earlyMailTips[2] },
              { key: "E4", tip: earlyMailTips[3] },
            ]
          : []),
        { key: "T1", tip: earlyTips[0] },
        { key: "T2", tip: earlyTips[1] },
        { key: "T3", tip: earlyTips[2] },
        { key: "T4", tip: earlyTips[3] },
        { key: "T5", tip: raceTips[0] },
        { key: "T6", tip: raceTips[1] },
        { key: "T7", tip: raceTips[2] },
        { key: "T8", tip: raceTips[3] },
      ];

      let winnerHorseNumber = extractHorseNumber(winner);
      if (!winnerHorseNumber && winnerSelectionId) {
        const matchingTip = tipData.find(
          ({ tip }) =>
            normalizeSelectionId(tip?.selectionId) === winnerSelectionId,
        );
        if (matchingTip?.tip) {
          winnerHorseNumber = extractHorseNumber(matchingTip.tip);
        }
      }

      // Keep T.Tot based on the standard 8 tips (T1-T8) so we don't inflate it
      // when Early Mail columns are enabled.
      const allTipNumbers = [
        earlyTips[0]?.formattedName?.split(".")[0] || "-",
        earlyTips[1]?.formattedName?.split(".")[0] || "-",
        earlyTips[2]?.formattedName?.split(".")[0] || "-",
        earlyTips[3]?.formattedName?.split(".")[0] || "-",
        raceTips[0]?.formattedName?.split(".")[0] || "-",
        raceTips[1]?.formattedName?.split(".")[0] || "-",
        raceTips[2]?.formattedName?.split(".")[0] || "-",
        raceTips[3]?.formattedName?.split(".")[0] || "-",
      ];
      totals.tipsTotal += calculateUniqueTips(allTipNumbers);

      let hasWinningTip = false;

      tipData.forEach(({ key, tip }) => {
        const hasTipInfo =
          tip?.formattedName ||
          tip?.selectionId ||
          tip?.runnerName ||
          tip?.name;
        if (hasTipInfo) {
          totals.tipWins[key].bets += 1;
        }

        const tipSelectionId = normalizeSelectionId(tip?.selectionId);
        const tipHorseNumber = extractHorseNumber(tip);
        const matchesWinnerById =
          winnerSelectionId &&
          tipSelectionId &&
          tipSelectionId === winnerSelectionId;
        const matchesWinnerByNumber =
          winnerHorseNumber &&
          tipHorseNumber &&
          tipHorseNumber === winnerHorseNumber;
        const tipIsWinner = matchesWinnerById || matchesWinnerByNumber;

        if (tipIsWinner) {
          totals.tipWins[key].count += 1;

          const {
            value: extractedOddsValue,
            source: extractedOddsSource,
            raw: rawTipOdds,
          } = extractTipOdds(tip);
          const oddsSource = Number.isFinite(extractedOddsValue)
            ? extractedOddsSource || "tipOdds"
            : Number.isFinite(winPrice)
              ? "winPrice"
              : "missing";
          const oddsApplied = Number.isFinite(extractedOddsValue)
            ? extractedOddsValue
            : Number.isFinite(winPrice)
              ? winPrice
              : null;

          if (Number.isFinite(extractedOddsValue)) {
            totals.tipWins[key].value += extractedOddsValue;
          } else if (Number.isFinite(winPrice)) {
            totals.tipWins[key].value += winPrice;
          } else {
            logger.log("Totals fallback missing odds", {
              track: debugTrack || "unknown",
              raceNumber: raceNumber || "?",
              tipPosition: key,
              rawTipOdds,
              winnerStartingPrice: winner?.startingPrice ?? null,
              winnerWinReturn: winner?.winReturn ?? null,
              tipReturns: tip?.returns ?? null,
              tipProfit: tip?.profit ?? null,
              tipWinReturn: tip?.winReturn ?? null,
              tipRaceProfit: tip?.raceProfit ?? null,
              tipData: tip || null,
            });
          }

          hasWinningTip = true;
          winnerDebugLines.push(
            `${key}(${matchesWinnerById ? "ID" : matchesWinnerByNumber ? "No" : "?"})`,
          );
          tipHitsForRace.push({
            tipKey: key,
            oddsApplied,
            oddsSource,
            rawTipOdds,
            winPrice,
            tipSelectionId,
            tipHorseNumber,
          });

          tipTotalsDebug[key].count += 1;
          if (oddsApplied !== null) {
            tipTotalsDebug[key].totalValue += oddsApplied;
          }
          tipTotalsDebug[key].entries.push({
            raceId: debugRaceId,
            track: debugTrack,
            raceNumber,
            oddsApplied,
            oddsSource,
            rawTipOdds,
            winPrice,
            tipSelectionId,
            tipHorseNumber,
          });
        }
      });

      if (hasWinningTip && Number.isFinite(winPrice)) {
        totals.winTotal += winPrice;
        winDebugEntries.push({
          raceId: debugRaceId,
          track: debugTrack,
          raceNumber,
          winPrice,
          winnerSelectionId,
          winnerHorseNumber,
          winners: tipHitsForRace.map((hit) => hit.tipKey),
        });
      }

      const logParts = [
        `Race ${raceNumber || "?"} @ ${debugTrack || "unknown"}`,
      ];
      // if (winnerDebugLines.length > 0) {
      //     logParts.push(`Winners: ${winnerDebugLines.join(', ')}`);
      //     logger.log(logParts.join(' | '));
      // } else {
      //     logParts.push('Winners: none matched');
      //     logger.log(logParts.join(' | '));
      // }

      raceDebugSummaries.push({
        raceId: debugRaceId,
        track: debugTrack,
        raceNumber,
        winnerSelectionId,
        winnerHorseNumber,
        winPrice,
        hasWinningTip,
        tipHits: tipHitsForRace,
        rawWinner: winner || null,
      });
    });

    return {
      totals,
      tipTotalsDebug,
      raceDebugSummaries,
      winDebugEntries,
    };
  }, [filteredRaces, showEarlyMailColumns, earlyMailTipsLookup]);

  const columnTotals = columnTotalsResult.totals;
  const tipTotalsDebug = columnTotalsResult.tipTotalsDebug;
  const raceDebugSummaries = columnTotalsResult.raceDebugSummaries;
  const winDebugEntries = columnTotalsResult.winDebugEntries;

  const totalWinningTips = useMemo(() => {
    return tipKeys.reduce(
      (sum, key) => sum + (columnTotals.tipWins[key]?.count || 0),
      0,
    );
  }, [columnTotals]);

  const isDebugEnvironment = (() => {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname;
    return host === "localhost" || host === "127.0.0.1";
  })();
  const [showDebugModal, setShowDebugModal] = useState(false);

  return (
    <Card className="overflow-hidden">
      {/* <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border-b">
                <div className="text-sm text-gray-600">
                    {selectedDate ? `Showing results for ${selectedDate}` : 'Showing all dates'}
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Date:</label>
                        <Select value={selectedDate} onValueChange={(val) => setSelectedDate(val === '__ALL__' ? '' : val)}>
                            <SelectTrigger className="w-48 px-2">
                                <SelectValue placeholder="All dates" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__ALL__">All</SelectItem>
                                {availableDates.map((d) => (
                                    <SelectItem key={d} value={d}>{d}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {selectedDate && (
                            <button
                                type="button"
                                onClick={() => setSelectedDate('')}
                                className="text-sm text-gray-600 hover:text-gray-900"
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    {isDebugEnvironment && (
                        <button
                            type="button"
                            onClick={() => setShowDebugModal(true)}
                            className="px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-500 shadow-sm"
                        >
                            Debug Totals
                        </button>
                    )}
                </div>
            </div> */}
      <div className="overflow-x-auto">
        <TooltipProvider delayDuration={300} skipDelayDuration={100}>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {[
                  !hideDateColumn ? (
                    <th
                      key="date"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("date")}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {getSortIcon("date")}
                      </div>
                    </th>
                  ) : null,
                  <th
                    key="time"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("time")}
                  >
                    <div className="flex items-center gap-1">
                      Time
                      {getSortIcon("time")}
                    </div>
                  </th>,
                  <th
                    key="track"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("track")}
                  >
                    <div className="flex items-center gap-1">
                      Track
                      {getSortIcon("track")}
                    </div>
                  </th>,
                  <th
                    key="race"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("race")}
                  >
                    <div className="flex items-center gap-1">
                      Race
                      {getSortIcon("race")}
                    </div>
                  </th>,
                  <th
                    key="horses"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("horses")}
                  >
                    <div className="flex items-center gap-1">
                      Horses
                      {getSortIcon("horses")}
                    </div>
                  </th>,
                  showEarlyMailColumns ? (
                    <th
                      key="early-mail-header"
                      className="px-2 py-1 text-center text-xs font-bold text-emerald-700 uppercase tracking-wider bg-emerald-100 border-b-2 border-emerald-300 border-x border-gray-200"
                      colSpan={showMaxHedge ? "5" : "4"}
                    >
                      Early Mail Tips
                    </th>
                  ) : null,
                  <th
                    key="morning-header"
                    className="px-2 py-1 text-center text-xs font-bold text-blue-700 uppercase tracking-wider bg-blue-100  border-b-2 border-blue-300"
                    colSpan={showMaxHedge ? "5" : "4"}
                  >
                    Race Day Tips
                  </th>,
                  <th
                    key="race-header"
                    className="px-2 py-1 text-center text-xs font-bold text-purple-700 uppercase tracking-wider bg-purple-100 border-b-2 border-purple-300 border-x border-gray-200"
                    colSpan={showMaxHedge ? "5" : "4"}
                  >
                    Late Mail Tips
                  </th>,
                  <th
                    key="ttot"
                    className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("uniqueTips")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      T.Tot
                      {getSortIcon("uniqueTips")}
                    </div>
                  </th>,
                  <th
                    key="first"
                    className="px-2 py-3 text-center text-xs font-medium text-yellow-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("first")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      1st
                      {getSortIcon("first")}
                    </div>
                  </th>,
                  showMaxHedge ? (
                    <th
                      key="combinedMaxHedge"
                      className="px-2 py-3 text-center text-xs font-medium text-orange-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("combinedMaxHedge")}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Odds
                        {getSortIcon("combinedMaxHedge")}
                      </div>
                    </th>
                  ) : null,
                  <th
                    key="win"
                    className="px-2 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("winPayout")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Win
                      {getSortIcon("winPayout")}
                    </div>
                  </th>,
                  <th
                    key="p1"
                    className="px-2 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider"
                  >
                    P1
                  </th>,
                  <th
                    key="p2"
                    className="px-2 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider"
                  >
                    P2
                  </th>,
                  <th
                    key="p3"
                    className="px-2 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider"
                  >
                    P3
                  </th>,
                ].filter(Boolean)}
              </tr>
              <tr>
                {[
                  ...(!hideDateColumn ? [<th key="d1"></th>] : []),
                  <th key="d2"></th>,
                  <th key="d3"></th>,
                  <th key="d4"></th>,
                  <th key="d5"></th>,
                  ...(showEarlyMailColumns
                    ? [
                        <th
                          key="e1"
                          className="px-2 py-2 text-center text-xs font-medium text-emerald-600 uppercase tracking-wider border-l border-gray-200"
                        >
                          E1
                        </th>,
                        <th
                          key="e2"
                          className="px-2 py-2 text-center text-xs font-medium text-emerald-600 uppercase tracking-wider"
                        >
                          E2
                        </th>,
                        <th
                          key="e3"
                          className="px-2 py-2 text-center text-xs font-medium text-emerald-600 uppercase tracking-wider"
                        >
                          E3
                        </th>,
                        <th
                          key="e4"
                          className={`px-2 py-2 text-center text-xs font-medium text-emerald-600 uppercase tracking-wider ${showMaxHedge ? "" : "border-r border-gray-200"}`}
                        >
                          E4
                        </th>,
                        ...(showMaxHedge
                          ? [
                              <th
                                key="ehedge"
                                className="px-2 py-2 text-center text-xs font-medium text-emerald-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-r border-gray-200"
                                onClick={() => handleSort("earlyMailMaxHedge")}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  Odds
                                  {getSortIcon("earlyMailMaxHedge")}
                                </div>
                              </th>,
                            ]
                          : []),
                      ]
                    : []),
                  <th
                    key="t1"
                    className="px-2 py-2 text-center text-xs font-medium text-blue-600 uppercase tracking-wider border-l border-gray-200"
                  >
                    T1
                  </th>,
                  <th
                    key="t2"
                    className="px-2 py-2 text-center text-xs font-medium text-blue-600 uppercase tracking-wider"
                  >
                    T2
                  </th>,
                  <th
                    key="t3"
                    className="px-2 py-2 text-center text-xs font-medium text-blue-600 uppercase tracking-wider"
                  >
                    T3
                  </th>,
                  <th
                    key="t4"
                    className={`px-2 py-2 text-center text-xs font-medium text-blue-600 uppercase tracking-wider ${showMaxHedge ? "" : "border-r border-gray-200"}`}
                  >
                    T4
                  </th>,
                  showMaxHedge ? (
                    <th
                      key="mhedge"
                      className="px-2 py-2 text-center text-xs font-medium text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-r border-gray-200"
                      onClick={() => handleSort("morningMaxHedge")}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Odds
                        {getSortIcon("morningMaxHedge")}
                      </div>
                    </th>
                  ) : null,
                  <th
                    key="t5"
                    className="px-2 py-2 text-center text-xs font-medium text-purple-600 uppercase tracking-wider border-l border-gray-200"
                  >
                    T5
                  </th>,
                  <th
                    key="t6"
                    className="px-2 py-2 text-center text-xs font-medium text-purple-600 uppercase tracking-wider"
                  >
                    T6
                  </th>,
                  <th
                    key="t7"
                    className="px-2 py-2 text-center text-xs font-medium text-purple-600 uppercase tracking-wider"
                  >
                    T7
                  </th>,
                  <th
                    key="t8"
                    className={`px-2 py-2 text-center text-xs font-medium text-purple-600 uppercase tracking-wider ${showMaxHedge ? "" : "border-r border-gray-200"}`}
                  >
                    T8
                  </th>,
                  showMaxHedge ? (
                    <th
                      key="rhedge"
                      className="px-2 py-2 text-center text-xs font-medium text-purple-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-r border-gray-200"
                      onClick={() => handleSort("raceTimeMaxHedge")}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Odds
                        {getSortIcon("raceTimeMaxHedge")}
                      </div>
                    </th>
                  ) : null,
                  <th key="totals-uniq"></th>,
                  <th key="totals-first"></th>,
                  showMaxHedge ? <th key="totals-hedge"></th> : null,
                  <th key="totals-win"></th>,
                  <th key="totals-p1"></th>,
                  <th key="totals-p2"></th>,
                  <th key="totals-p3"></th>,
                ].filter(Boolean)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(() => {
                const groupSpacingKeys = new Set([
                  "track",
                  "race",
                  "horses",
                  "uniqueTips",
                ]);
                const shouldShowGrouping = groupSpacingKeys.has(sortConfig.key);
                const totalColumns =
                  (showMaxHedge ? 22 : 19) +
                  (showEarlyMailColumns ? (showMaxHedge ? 5 : 4) : 0) +
                  (hideDateColumn ? -1 : 0);
                const rows = [];

                for (let index = 0; index < filteredRaces.length; index++) {
                  const race = filteredRaces[index];
                  // Parse race data for display
                  const raceId = race.id || "";
                  const parts = raceId.split("-");
                  const raceNumber = parts[parts.length - 4] || "";
                  const date = getRaceDate(race) || "";
                  const raceTimeLabel = getRaceTimeDisplay(race);
                  const trackNameDisplay =
                    race.meetingName ||
                    parts.slice(0, parts.length - 4).join(" ");

                  const { earlyTips, raceTips, tipNumbers } = getTipsData(race);
                  const [t1, t2, t3, t4, t5, t6, t7, t8] = tipNumbers;

                  // Calculate unique tips total
                  const totalUniqueTips = calculateUniqueTips(tipNumbers);

                  // Get actual results from betfair results
                  const betfairResults = race.betfairResults || {};
                  const allTips = [...earlyTips, ...raceTips];
                  // Extract winner and place getters from betfairResults structure
                  const winner = betfairResults.winner;
                  const placeGettersAll = betfairResults.placeGetters || [];
                  const winnerSelectionId = winner?.selectionId;

                  // Get first place horse number using winner's selectionId
                  const firstPlace = winner
                    ? getHorseNumber(winner.selectionId, allTips)
                    : "-";

                  // Get our tip's selectionId for comparison
                  const ourTipSelectionId =
                    raceTips[0]?.selectionId || earlyTips[0]?.selectionId;

                  // Number of runners for the race
                  const numberOfRunners =
                    betfairResults?.numberOfRunners ?? "-";

                  // Check if our tip won - compare selectionIds
                  const winPrice = winner?.startingPrice || "";

                  // Check if our tip placed - find place price excluding winner
                  const placePrice =
                    placeGettersAll.find(
                      (placer) =>
                        placer.selectionId === ourTipSelectionId &&
                        placer.selectionId !== winner?.selectionId,
                    )?.startingPrice || null;

                  // If there are only 2 place getters and one is the winner, use the other one for P1
                  const winnerSelectionIdNorm =
                    normalizeSelectionId(winnerSelectionId);
                  const orderedPlaceSlots = (() => {
                    if (!placeGettersAll.length) return [];
                    const winnerEntry = winnerSelectionIdNorm
                      ? placeGettersAll.find(
                          (p) =>
                            normalizeSelectionId(p?.selectionId) ===
                            winnerSelectionIdNorm,
                        )
                      : null;
                    const others = winnerSelectionIdNorm
                      ? placeGettersAll.filter(
                          (p) =>
                            normalizeSelectionId(p?.selectionId) !==
                            winnerSelectionIdNorm,
                        )
                      : placeGettersAll.slice(1);
                    return winnerEntry
                      ? [winnerEntry, ...others]
                      : placeGettersAll;
                  })();
                  const p1Price = orderedPlaceSlots[0]?.startingPrice || null;
                  const p2Price = orderedPlaceSlots[1]?.startingPrice || null;
                  const p3Price = orderedPlaceSlots[2]?.startingPrice || null;

                  // Place getters with the winner removed, used to give placed tips
                  // a silver highlight alongside the winner's orange. placeGetters
                  // sometimes includes the winner and sometimes doesn't, so filter
                  // on selectionId rather than assuming a fixed slot.
                  const placeOnlySlots = winnerSelectionIdNorm
                    ? placeGettersAll.filter(
                        (p) =>
                          normalizeSelectionId(p?.selectionId) !==
                          winnerSelectionIdNorm,
                      )
                    : placeGettersAll;

                  // Calculate max hedge
                  const maxHedge = calculateMaxHedge(earlyTips, raceTips);

                  // Helper function to render tip cell with tooltip
                  const renderTipCell = (
                    tipData,
                    tipNumber,
                    palette = "raceDay",
                    extraClassName = "",
                  ) => {
                    const isEarlyMail = palette === "earlyMail";
                    const isRaceDay = palette === "raceDay";

                    if (!tipData || !tipData.formattedName) {
                      return (
                        <td
                          className={`px-2 py-3 text-center text-sm ${
                            isEarlyMail
                              ? "text-emerald-900 bg-emerald-50"
                              : isRaceDay
                                ? "text-blue-900 bg-blue-50"
                                : "text-purple-900 bg-purple-50"
                          } ${extraClassName}`}
                        >
                          <span>-</span>
                        </td>
                      );
                    }

                    const horseNumber = tipData.formattedName.split(".")[0];
                    const horseName = tipData.formattedName
                      .split(".")
                      .slice(1)
                      .join(".")
                      .trim();
                    const odds =
                      tipData.lastPriceTraded || tipData.currentPrice;
                    const winnerHorseNumber = extractHorseNumber(winner);
                    const tipHorseNumber =
                      extractHorseNumber(tipData) || horseNumber;
                    const isWinner =
                      tipData.selectionId === winnerSelectionId ||
                      (winnerHorseNumber &&
                        tipHorseNumber &&
                        tipHorseNumber === winnerHorseNumber);

                    // Check if this horse number matches the first place
                    const matchesFirstPlace = horseNumber === firstPlace;

                    // Placed (2nd/3rd) but did not win — gets the silver highlight
                    const tipSelectionIdNorm = normalizeSelectionId(
                      tipData.selectionId,
                    );
                    const isPlaceGetter =
                      !isWinner &&
                      !matchesFirstPlace &&
                      placeOnlySlots.some((placer) => {
                        const placerSelectionId = normalizeSelectionId(
                          placer?.selectionId,
                        );
                        if (placerSelectionId && tipSelectionIdNorm) {
                          return placerSelectionId === tipSelectionIdNorm;
                        }
                        const placerHorseNumber = extractHorseNumber(placer);
                        return Boolean(
                          placerHorseNumber &&
                            tipHorseNumber &&
                            placerHorseNumber === tipHorseNumber,
                        );
                      });

                    // Calculate win chance and implied probability
                    const randomWinChance = calculateWinChance(numberOfRunners);
                    const impliedProbability =
                      calculateImpliedProbability(odds);

                    // Check fluctuation - ensure we have valid arrays
                    const safeEarlyTips = Array.isArray(earlyTips)
                      ? earlyTips
                      : [];
                    const safeRaceTips = Array.isArray(raceTips)
                      ? raceTips
                      : [];
                    const fluctuation = getTipFluctuation(
                      safeEarlyTips,
                      safeRaceTips,
                      horseNumber,
                    );

                    const tooltipContent = (
                      <div className="space-y-3">
                        {/* Horse Name Header */}
                        <div className="text-center pb-2 border-b border-gray-200">
                          <div className="text-base font-semibold text-gray-800">
                            {horseName}
                          </div>
                          <div className="text-xs text-gray-600">
                            Horse #{horseNumber}
                          </div>
                        </div>

                        {/* Main Stats Grid */}
                        <div className="grid grid-cols-2 gap-3">
                          {odds && (
                            <div className="text-center p-2 bg-blue-50 rounded">
                              <div className="text-lg font-bold text-blue-600">
                                ${parseFloat(odds).toFixed(2)}
                              </div>
                              <div className="text-xs text-blue-700 font-medium">
                                Odds
                              </div>
                            </div>
                          )}

                          {randomWinChance && (
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <div className="text-lg font-bold text-gray-600">
                                {randomWinChance}%
                              </div>
                              <div className="text-xs text-gray-700 font-medium">
                                Random Chance
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Probability Analysis */}
                        {impliedProbability && odds && randomWinChance && (
                          <div className="text-center p-2 bg-purple-50 rounded">
                            <div className="text-sm font-semibold text-purple-600 mb-1">
                              {impliedProbability}% Implied Probability
                            </div>
                            <div
                              className={`text-xs font-medium ${parseFloat(impliedProbability) > parseFloat(randomWinChance) ? "text-red-600" : "text-green-600"}`}
                            >
                              {parseFloat(impliedProbability) >
                              parseFloat(randomWinChance)
                                ? "📈 Overvalued"
                                : "📉 Undervalued"}
                            </div>
                          </div>
                        )}

                        {/* Fluctuation Status */}
                        {fluctuation && (
                          <div className="text-center p-2 bg-orange-50 rounded">
                            <div className="flex items-center justify-center gap-1 mb-1">
                              {fluctuation === "up" && (
                                <ChevronUp className="w-4 h-4 text-green-500" />
                              )}
                              {fluctuation === "down" && (
                                <ChevronDown className="w-4 h-4 text-red-500" />
                              )}
                              <span
                                className={`text-sm font-semibold ${fluctuation === "up" ? "text-green-600" : fluctuation === "down" ? "text-red-600" : "text-gray-600"}`}
                              >
                                {fluctuation === "up"
                                  ? "Improved Position"
                                  : fluctuation === "down"
                                    ? "Declined Position"
                                    : "Same Position"}
                              </span>
                            </div>
                            <div className="text-xs text-orange-700 font-medium">
                              Morning vs Race Time
                            </div>
                          </div>
                        )}

                        {/* Winner Badge */}
                        {isWinner && (
                          <div className="text-center p-2 bg-gradient-to-r from-yellow-100 to-orange-100 rounded border border-yellow-300">
                            <div className="flex items-center justify-center gap-2">
                              <TrophyIcon className="w-4 h-4 text-yellow-600" />
                              <span className="text-sm font-bold text-yellow-700">
                                Race Winner!
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Placed Badge */}
                        {isPlaceGetter && (
                          <div className="text-center p-2 bg-gradient-to-r from-slate-100 to-slate-200 rounded border border-slate-300">
                            <div className="flex items-center justify-center gap-2">
                              <MedalIcon className="w-4 h-4 text-slate-600" />
                              <span className="text-sm font-bold text-slate-700">
                                Placed!
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );

                    return (
                      <td
                        key={`${tipNumber}-${horseNumber}`}
                        className={`px-2 py-3 text-center text-sm ${
                          isWinner
                            ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-white font-bold"
                            : matchesFirstPlace
                              ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-white font-bold"
                              : isPlaceGetter
                                ? "bg-gradient-to-br from-slate-200 to-slate-400 text-slate-900 font-semibold"
                                : isEarlyMail
                                  ? "text-emerald-900 bg-emerald-50 hover:bg-emerald-100"
                                  : isRaceDay
                                    ? "text-blue-900 bg-blue-50 hover:bg-blue-100"
                                    : "text-purple-900 bg-purple-50 hover:bg-purple-100"
                        } ${extraClassName}`}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="relative flex flex-col items-center cursor-pointer w-full h-full">
                              {(isWinner || matchesFirstPlace) && (
                                <TrophyIcon className="absolute -top-1 -right-1 w-3 h-3 text-yellow-200" />
                              )}
                              <span
                                className={
                                  isWinner || matchesFirstPlace
                                    ? "font-bold"
                                    : ""
                                }
                              >
                                {horseNumber}
                              </span>
                              {odds && (
                                <span className="text-xs font-normal text-gray-600">
                                  ${parseFloat(odds).toFixed(2)}
                                </span>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            align="center"
                            sideOffset={5}
                            className="p-4 bg-white border border-gray-200 shadow-xl rounded-lg max-w-xs z-50"
                          >
                            {tooltipContent}
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    );
                  };

                  const groupValue = (() => {
                    switch (sortConfig.key) {
                      case "track":
                        return trackNameDisplay || "";
                      case "race":
                        return raceNumber || "";
                      case "horses":
                        return numberOfRunners ?? "";
                      case "uniqueTips":
                        return totalUniqueTips;
                      default:
                        return null;
                    }
                  })();

                  rows.push(
                    <tr
                      key={`${race.id || "race"}-${index}`}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      {!hideDateColumn && (
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {date}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {raceTimeLabel}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {trackNameDisplay}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        R{raceNumber}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {numberOfRunners}
                      </td>

                      {/* Early Mail Tips (E1-E4) */}
                      {showEarlyMailColumns &&
                        (() => {
                          const lookupKey = `${normalizeTrackNameForLookup(trackNameDisplay)}|${raceNumber}`;
                          const earlyMailTips =
                            earlyMailTipsLookup?.get?.(lookupKey) || [];
                          const earlyMailMaxHedge =
                            calculateEarlyMailMaxHedge(earlyMailTips);
                          const groupLeftBorder = "border-l border-gray-200";
                          const groupRightBorder = "border-r border-gray-200";
                          const e4RightBorder = showMaxHedge
                            ? ""
                            : groupRightBorder;
                          return (
                            <>
                              {renderTipCell(
                                earlyMailTips[0],
                                "E1",
                                "earlyMail",
                                groupLeftBorder,
                              )}
                              {renderTipCell(
                                earlyMailTips[1],
                                "E2",
                                "earlyMail",
                              )}
                              {renderTipCell(
                                earlyMailTips[2],
                                "E3",
                                "earlyMail",
                              )}
                              {renderTipCell(
                                earlyMailTips[3],
                                "E4",
                                "earlyMail",
                                e4RightBorder,
                              )}
                              {showMaxHedge && (
                                <td className="px-2 py-3 text-center text-sm text-emerald-900 bg-emerald-50 border-r border-gray-200">
                                  <span
                                    className={`px-1 py-1 rounded text-xs font-medium ${
                                      parseFloat(earlyMailMaxHedge) <
                                        maxHedgeWinRate &&
                                      parseFloat(earlyMailMaxHedge) > 0
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                  >
                                    {earlyMailMaxHedge}
                                  </span>
                                </td>
                              )}
                            </>
                          );
                        })()}

                      {/* Race Day Tips (T1-T4) */}
                      {renderTipCell(
                        earlyTips[0],
                        "T1",
                        "raceDay",
                        "border-l border-gray-200",
                      )}
                      {renderTipCell(earlyTips[1], "T2", "raceDay")}
                      {renderTipCell(earlyTips[2], "T3", "raceDay")}
                      {renderTipCell(
                        earlyTips[3],
                        "T4",
                        "raceDay",
                        showMaxHedge ? "" : "border-r border-gray-200",
                      )}

                      {/* Morning Max Hedge */}
                      {showMaxHedge && (
                        <td className="px-2 py-3 text-center text-sm text-blue-900 bg-blue-50 border-r border-gray-200">
                          <span
                            className={`px-1 py-1 rounded text-xs font-medium ${
                              parseFloat(calculateMorningMaxHedge(earlyTips)) <
                                maxHedgeWinRate &&
                              parseFloat(calculateMorningMaxHedge(earlyTips)) >
                                0
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {calculateMorningMaxHedge(earlyTips)}
                          </span>
                        </td>
                      )}

                      {/* Late Mail Tips (T5-T8) */}
                      {renderTipCell(
                        raceTips[0],
                        "T5",
                        "lateMail",
                        "border-l border-gray-200",
                      )}
                      {renderTipCell(raceTips[1], "T6", "lateMail")}
                      {renderTipCell(raceTips[2], "T7", "lateMail")}
                      {renderTipCell(
                        raceTips[3],
                        "T8",
                        "lateMail",
                        showMaxHedge ? "" : "border-r border-gray-200",
                      )}

                      {/* Race-time Max Hedge */}
                      {showMaxHedge && (
                        <td className="px-2 py-3 text-center text-sm text-purple-900 bg-purple-50 border-r border-gray-200">
                          <span
                            className={`px-1 py-1 rounded text-xs font-medium ${
                              parseFloat(calculateRaceTimeMaxHedge(raceTips)) <
                                maxHedgeWinRate &&
                              parseFloat(calculateRaceTimeMaxHedge(raceTips)) >
                                0
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {calculateRaceTimeMaxHedge(raceTips)}
                          </span>
                        </td>
                      )}

                      {/* Total Unique Tips */}
                      <td className="px-2 py-3 text-center text-sm font-medium text-gray-900">
                        {totalUniqueTips}
                      </td>

                      {/* Race Results - 1st place with orange background if matches tips */}
                      <td
                        className={`px-2 py-3 text-center text-sm font-medium ${
                          tipNumbers.includes(firstPlace)
                            ? "bg-gradient-to-br from-orange-400 to-orange-500 text-white"
                            : "bg-yellow-50 text-yellow-900"
                        }`}
                      >
                        {firstPlace}
                      </td>

                      {/* Other Max Hedge Column */}
                      {showMaxHedge && (
                        <td className="px-2 py-3 text-center text-sm font-medium">
                          {maxHedge !== "-" ? (
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                parseFloat(maxHedge) < maxHedgeWinRate &&
                                parseFloat(maxHedge) > 0
                                  ? "bg-green-100 text-green-800"
                                  : "bg-orange-100 text-orange-800"
                              }`}
                            >
                              {parseFloat(maxHedge).toFixed(3)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      )}

                      {/* Win/Place Prices based on betfairResults */}
                      <td className="px-2 py-3 text-center text-sm">
                        {winPrice ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ${parseFloat(winPrice).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-center text-sm">
                        {!isNaN(parseFloat(p1Price)) && isFinite(p1Price) ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ${parseFloat(p1Price).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-center text-sm">
                        {!isNaN(parseFloat(p2Price)) && isFinite(p2Price) ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-blue-800">
                            ${parseFloat(p2Price).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-center text-sm">
                        {!isNaN(parseFloat(p3Price)) && isFinite(p3Price) ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-blue-800">
                            ${parseFloat(p3Price).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>,
                  );

                  if (shouldShowGrouping) {
                    const nextRace = filteredRaces[index + 1];
                    const nextGroupValue = nextRace
                      ? (() => {
                          const nextParts = (nextRace.id || "").split("-");
                          const nextRaceNumber =
                            nextParts[nextParts.length - 4] || "";
                          const nextTrackName =
                            nextRace.meetingName ||
                            nextParts.slice(0, nextParts.length - 4).join(" ");
                          const { tipNumbers: nextTipNumbers } =
                            getTipsData(nextRace);
                          const nextUniqueTips =
                            calculateUniqueTips(nextTipNumbers);
                          const nextRunners =
                            nextRace.betfairResults?.numberOfRunners ?? "";

                          switch (sortConfig.key) {
                            case "track":
                              return nextTrackName || "";
                            case "race":
                              return nextRaceNumber || "";
                            case "horses":
                              return nextRunners;
                            case "uniqueTips":
                              return nextUniqueTips;
                            default:
                              return null;
                          }
                        })()
                      : null;

                    if (groupValue !== nextGroupValue) {
                      rows.push(
                        <tr key={`group-spacer-${race.id || index}`}>
                          <td colSpan={totalColumns} className="h-4"></td>
                        </tr>,
                      );
                    }
                  }
                }

                return rows;
              })()}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={hideDateColumn ? 2 : 3}
                  className={`${stickyCellClass} px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-right`}
                >
                  Totals
                </td>
                <td
                  className={`${stickyCellClass} px-4 py-3 text-sm font-semibold text-gray-900`}
                >
                  {formatInteger(columnTotals.races)}
                </td>
                <td
                  className={`${stickyCellClass} px-4 py-3 text-sm font-semibold text-gray-900`}
                >
                  {formatInteger(columnTotals.horses)}
                </td>
                {showEarlyMailColumns &&
                  ["E1", "E2", "E3", "E4"].map((key) => {
                    const tipSummary = columnTotals.tipWins[key] || {
                      count: 0,
                      value: 0,
                      bets: 0,
                    };
                    return (
                      <td
                        key={`totals-${key}`}
                        className={tipTotalsCellClassEarlyMail}
                      >
                        <div className="text-xs font-semibold text-emerald-700">
                          {formatInteger(tipSummary.count)}
                        </div>
                        <div className="text-[10px] font-medium text-emerald-500">
                          {formatCurrency(tipSummary.value)}
                        </div>
                        <div className="text-[10px] font-medium text-emerald-600">
                          {formatRoiPercentage(
                            tipSummary.value || 0,
                            tipSummary.bets || 0,
                          )}
                        </div>
                      </td>
                    );
                  })}
                {showEarlyMailColumns && showMaxHedge && (
                  <td
                    className={`${stickyCellClass} px-2 py-3 text-center text-xs text-emerald-600 border-r border-gray-200`}
                  >
                    -
                  </td>
                )}
                {earlyTipKeys.map((key) => {
                  const tipSummary = columnTotals.tipWins[key] || {
                    count: 0,
                    value: 0,
                    bets: 0,
                  };
                  return (
                    <td
                      key={`totals-${key}`}
                      className={tipTotalsCellClassEarly}
                    >
                      <div className="text-xs font-semibold text-blue-700">
                        {formatInteger(tipSummary.count)}
                      </div>
                      <div className="text-[10px] font-medium text-blue-500">
                        {formatCurrency(tipSummary.value)}
                      </div>
                      <div className="text-[10px] font-medium text-blue-600">
                        {formatRoiPercentage(
                          tipSummary.value || 0,
                          tipSummary.bets || 0,
                        )}
                      </div>
                    </td>
                  );
                })}
                {showMaxHedge && (
                  <td
                    className={`${stickyCellClass} px-2 py-3 text-center text-xs text-blue-600`}
                  >
                    -
                  </td>
                )}
                {raceTipKeys.map((key) => {
                  const tipSummary = columnTotals.tipWins[key] || {
                    count: 0,
                    value: 0,
                    bets: 0,
                  };
                  return (
                    <td
                      key={`totals-${key}`}
                      className={tipTotalsCellClassRace}
                    >
                      <div className="text-xs font-semibold text-purple-700">
                        {formatInteger(tipSummary.count)}
                      </div>
                      <div className="text-[10px] font-medium text-purple-500">
                        {formatCurrency(tipSummary.value)}
                      </div>
                      <div className="text-[10px] font-medium text-purple-600">
                        {formatRoiPercentage(
                          tipSummary.value || 0,
                          tipSummary.bets || 0,
                        )}
                      </div>
                    </td>
                  );
                })}
                {showMaxHedge && (
                  <td
                    className={`${stickyCellClass} px-2 py-3 text-center text-xs text-purple-600`}
                  >
                    -
                  </td>
                )}
                <td
                  className={`${stickyCellClass} px-2 py-3 text-center text-sm font-semibold text-gray-900`}
                >
                  {formatInteger(columnTotals.tipsTotal)}
                </td>
                <td className={`${stickyCellClass} px-2 py-3 text-center`}>
                  <div className="text-xs font-semibold text-yellow-700">
                    {formatInteger(totalWinningTips)}
                  </div>
                  <div className="text-[10px] font-medium text-yellow-500">
                    Tips Hit
                  </div>
                </td>
                {showMaxHedge && (
                  <td
                    className={`${stickyCellClass} px-2 py-3 text-center text-xs font-semibold text-orange-600`}
                  >
                    -
                  </td>
                )}
                <td className={`${stickyCellClass} px-2 py-3 text-center`}>
                  <div className="text-xs font-semibold text-green-700">
                    {formatCurrency(columnTotals.winTotal)}
                  </div>
                  <div className="text-[10px] font-medium text-green-500">
                    $ Won
                  </div>
                </td>
                <td className={`${stickyCellClass} px-2 py-3 text-center`}>
                  <div className="text-xs font-semibold text-green-700">
                    {formatCurrency(columnTotals.place1Total)}
                  </div>
                  <div className="text-[10px] font-medium text-green-500">
                    P1 $
                  </div>
                </td>
                <td className={`${stickyCellClass} px-2 py-3 text-center`}>
                  <div className="text-xs font-semibold text-green-700">
                    {formatCurrency(columnTotals.place2Total)}
                  </div>
                  <div className="text-[10px] font-medium text-green-500">
                    P2 $
                  </div>
                </td>
                <td className={`${stickyCellClass} px-2 py-3 text-center`}>
                  <div className="text-xs font-semibold text-green-700">
                    {formatCurrency(columnTotals.place3Total)}
                  </div>
                  <div className="text-[10px] font-medium text-green-500">
                    P3 $
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </TooltipProvider>
      </div>

      {(!filteredRaces || filteredRaces.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          No archive data available for {trackName}
          {selectedDate ? ` on ${selectedDate}` : ""}
        </div>
      )}

      {isDebugEnvironment && showDebugModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowDebugModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-[min(95vw,1100px)] max-h-[90vh] overflow-y-auto p-6 space-y-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Totals Debug Inspector
                </h2>
                <p className="text-sm text-gray-600">
                  Review which rows contribute to each totals cell. Counts and
                  sums should mirror the footer.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDebugModal(false)}
                className="px-3 py-1.5 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>

            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Tip Totals Summary
              </h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        Tip
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">
                        Footer Count
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">
                        Computed Count
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">
                        Footer $
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">
                        Computed $
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        Entries
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tipKeys.map((tipKey) => {
                      const footerData = columnTotals.tipWins[tipKey] || {
                        count: 0,
                        value: 0,
                      };
                      const debugData = tipTotalsDebug?.[tipKey] || {
                        count: 0,
                        totalValue: 0,
                        entries: [],
                      };
                      return (
                        <tr
                          key={`debug-summary-${tipKey}`}
                          className="bg-white"
                        >
                          <td className="px-3 py-2 font-semibold text-gray-900">
                            {tipKey}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-800">
                            {footerData.count}
                          </td>
                          <td
                            className={`px-3 py-2 text-right ${footerData.count === debugData.count ? "text-gray-800" : "text-red-600 font-semibold"}`}
                          >
                            {debugData.count}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-800">
                            {footerData.value
                              ? footerData.value.toFixed(2)
                              : "0.00"}
                          </td>
                          <td
                            className={`px-3 py-2 text-right ${Math.abs((footerData.value || 0) - (debugData.totalValue || 0)) < 0.0001 ? "text-gray-800" : "text-red-600 font-semibold"}`}
                          >
                            {debugData.totalValue
                              ? debugData.totalValue.toFixed(2)
                              : "0.00"}
                          </td>
                          <td className="px-3 py-2 text-left text-gray-700">
                            {debugData.entries.length > 0 ? (
                              <ul className="space-y-1">
                                {debugData.entries.map((entry, idx) => (
                                  <li
                                    key={`${tipKey}-entry-${idx}`}
                                    className="text-xs text-gray-600"
                                  >
                                    <span className="font-medium text-gray-800">
                                      Race {entry.raceNumber || "?"} @{" "}
                                      {entry.track || "unknown"}:
                                    </span>{" "}
                                    <span>
                                      odds=
                                      {entry.oddsApplied !== null &&
                                      entry.oddsApplied !== undefined
                                        ? entry.oddsApplied
                                        : "null"}{" "}
                                      ({entry.oddsSource})
                                    </span>{" "}
                                    <span className="text-gray-400">
                                      rawTipOdds={entry.rawTipOdds ?? "null"},
                                      winPrice={entry.winPrice ?? "null"}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-xs text-gray-400">
                                No entries
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">
                1st Column (Win $) Entries
              </h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        Race
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        Track
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">
                        $ Added
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        Winning Tips
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        Winner Selection
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {winDebugEntries.length > 0 ? (
                      winDebugEntries.map((entry, idx) => (
                        <tr key={`win-entry-${idx}`} className="bg-white">
                          <td className="px-3 py-2 text-gray-800 font-medium">
                            {entry.raceNumber || "?"}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            {entry.track || "unknown"}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-800">
                            {entry.winPrice !== null &&
                            entry.winPrice !== undefined
                              ? entry.winPrice.toFixed(2)
                              : "null"}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            {entry.winners?.length
                              ? entry.winners.join(", ")
                              : "N/A"}
                          </td>
                          <td className="px-3 py-2 text-gray-500 text-xs">
                            selId={entry.winnerSelectionId ?? "null"} / horse=
                            {entry.winnerHorseNumber ?? "null"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-3 py-3 text-center text-sm text-gray-500"
                        >
                          No win entries captured.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Race-by-Race Winners
              </h3>
              <div className="space-y-3">
                {raceDebugSummaries.map((race) => (
                  <div
                    key={`race-debug-${race.raceId}`}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    <div className="flex flex-wrap gap-6 text-sm text-gray-700">
                      <div>
                        <span className="font-semibold text-gray-900">
                          Race:
                        </span>{" "}
                        {race.raceNumber || "?"}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">
                          Track:
                        </span>{" "}
                        {race.track || "unknown"}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">
                          Winner selId:
                        </span>{" "}
                        {race.winnerSelectionId ?? "null"}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">
                          Winner horse #:
                        </span>{" "}
                        {race.winnerHorseNumber ?? "null"}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">
                          Win price:
                        </span>{" "}
                        {race.winPrice !== null && race.winPrice !== undefined
                          ? race.winPrice
                          : "null"}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">
                          Matched tip?
                        </span>{" "}
                        {race.hasWinningTip ? "Yes" : "No"}
                      </div>
                    </div>
                    <div className="mt-3">
                      <span className="text-sm font-semibold text-gray-900">
                        Tip hits:
                      </span>
                      {race.tipHits.length > 0 ? (
                        <ul className="mt-1 space-y-1 text-xs text-gray-600">
                          {race.tipHits.map((hit, idx) => (
                            <li key={`race-${race.raceId}-hit-${idx}`}>
                              {hit.tipKey} → odds=
                              {hit.oddsApplied !== null &&
                              hit.oddsApplied !== undefined
                                ? hit.oddsApplied
                                : "null"}{" "}
                              ({hit.oddsSource}) | rawTipOdds=
                              {hit.rawTipOdds ?? "null"} | winPrice=
                              {hit.winPrice ?? "null"} | selId=
                              {hit.tipSelectionId ?? "null"} | horse=
                              {hit.tipHorseNumber ?? "null"}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-500">
                          No matching tips recorded.
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ResultsArchiveTable;
